import { AlertSettings } from '@interfaces/models/alertSettings';
import { Alert, AlertNotificationState } from '@interfaces/models/alert';
import { getAxiosConfig, axios } from '@config/axios';
import Environment from '@config/index';
import { Env } from '@interfaces/models/environment';
import { AlertOrderType } from '@interfaces/models/alert';
import { AlertCriterion, AlertCriterionKey } from '@interfaces/models/alertCriterion';
import { ProductsAverageMatching } from '@interfaces/api/responses/products-average-matching';
import { ProductsAverageMatchingPayload } from '@interfaces/api/payloads/products-average-matching';
import { ApiResponse } from '@interfaces/api';

export type AlertsResponse = ApiResponse<Alert[]>;

export interface ListItem {
  id: number;
  label: string;
}

export interface AlertsQuery {
  limit?: number;
  offset?: number;
  order?: AlertOrderType;
}

export interface NewInsAlertsResponse {
  alerts: Alert[];
  metaData?: PaginationState;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
}

export interface AlertsExistsResponse {
  exists: boolean;
  id: string;
}

export default class AlertsService {
  static apiBaseUrl: Env['apiBaseUrl'] = Environment.apiBaseUrl;

  static async createAlert(payload: AlertSettings) {
    const axiosConfig = getAxiosConfig();
    return axios.post<AlertSettings, ApiResponse<{ data: Alert }>>(`${this.apiBaseUrl}/alerts`, payload, axiosConfig);
  }

  private static async getAlertsResponse(
    version: string,
    offset: number,
    limit: number,
    order?: AlertOrderType,
    path = '',
  ): Promise<AlertsResponse> {
    try {
      const axiosConfig = getAxiosConfig();
      axiosConfig.params = {
        ...axiosConfig.params,
        version,
        limit,
        offset,
        ...(order && { order }),
      };

      const { data } = await axios.get(`${this.apiBaseUrl}/alerts${path}`, axiosConfig);
      return data;
    } catch (err) {
      const { status, data } = err?.response;
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw {
        status,
        message: data?.errors?.detail,
      };
    }
  }

  static async getAlertsNewin({ limit, offset, order }: AlertsQuery): Promise<AlertsResponse> {
    return this.getAlertsResponse('', offset, limit, order, '/newin');
  }

  static async getAlerts(version: string, offset: number, limit: number, order: AlertOrderType): Promise<Alert[]> {
    try {
      const { data } = await this.getAlertsResponse(version, offset, limit, order);
      return data;
    } catch (err) {
      throw err?.message;
    }
  }

  static async deleteAlert(alertId: string, alertType: string): Promise<void> {
    try {
      const axiosConfig = getAxiosConfig();
      await axios.delete(`${this.apiBaseUrl}/alerts/${encodeURIComponent(String(alertId))}`, {
        data: {
          type: alertType,
        },
        ...axiosConfig,
      });
    } catch (error) {
      throw error?.response?.data?.errors;
    }
  }

  static async resetCounter(alertId: string) {
    try {
      const axiosConfig = getAxiosConfig();
      await axios.patch(
        `${this.apiBaseUrl}/alerts/newin/${encodeURIComponent(alertId)}/reset-counter`,
        null,
        axiosConfig,
      );
    } catch (error) {
      throw error?.response?.data?.errors?.detail;
    }
  }

  static async updateNotificationState(alertId: string, payload: AlertNotificationState) {
    try {
      const axiosConfig = getAxiosConfig();
      await axios.patch(
        `${this.apiBaseUrl}/alerts/newin/${encodeURIComponent(String(alertId))}/notification-state`,
        payload,
        axiosConfig,
      );
    } catch (error) {
      throw error?.response?.data?.errors?.detail;
    }
  }

  static async getProductsAverageMatching(
    payload: ProductsAverageMatchingPayload,
  ): Promise<ProductsAverageMatching['data']> {
    const axiosConfig = getAxiosConfig();
    const queryParameters: URLSearchParams = new URLSearchParams();

    Object.keys(payload).forEach((alertCriterionKey: AlertCriterionKey) => {
      const hasArrayValues = Array.isArray(payload[alertCriterionKey]);

      if (hasArrayValues) {
        (payload[alertCriterionKey] as string[]).forEach((criterionValue: string) => {
          queryParameters.append(alertCriterionKey, criterionValue);
        });
      } else {
        queryParameters.set(alertCriterionKey, payload[alertCriterionKey] as string);
      }
    });

    const { data } = await axios.get<ProductsAverageMatching>(
      `${this.apiBaseUrl}/alerts/average-matching-products?${queryParameters}`,
      axiosConfig,
    );

    return data.data;
  }

  static async checkAlertExistence(alertCriteria: AlertCriterion[]) {
    const axiosConfig = getAxiosConfig();
    const data = await axios.post<ApiResponse<AlertsExistsResponse>>(
      `${Environment.apiBaseUrl}/alerts/exists`,
      { criteria: alertCriteria },
      axiosConfig,
    );
    return data.data;
  }
}
